/**
 * The brand, as data (ADR-0029).
 *
 * What lives here is what both clients genuinely share: the palette, the
 * radii, the 4pt unit, and the shadow a card sits on. What does NOT live here
 * is the scale each client builds on top -- mobile reads at fourteen inches
 * and the web at two feet, and pretending one type ramp serves both is what
 * made the site read as a phone layout stretched wide.
 *
 * **Plain JavaScript, deliberately.** `scripts/web-tokens.mjs` runs in the
 * gate under bare `node`, and `apps/mobile/src/theme.ts` is bundled by Metro.
 * A `.ts` source would need a loader in the first and a resolution rule in the
 * second, and Metro resolution is where this repository has been bitten most
 * (ADR-0029). Types are declared alongside in `index.d.ts`.
 *
 * Nothing here may import a platform: no `react-native`, no `Platform`, no
 * CSS. The moment it does, the gate stops being able to read it and ADR-0025's
 * textual parse comes back.
 */

/**
 * Shake Pass Play (WI-085).
 *
 * Eleven values from the pack, sixteen roles in the product. Every one below
 * is either from the pack verbatim or marked DERIVED with its reason. Nothing
 * is picked by eye.
 *
 * The three hues are put to work rather than used as decoration: **pass** is
 * the primary action because green reads as go, **play** marks a win because
 * a win is the celebratory one, and **shake** carries destruction because it
 * is the only warm hue in the palette.
 */
export const palette = {
  /** Body text. `--spp-ink`. */
  ink: "#26215C",
  /** Dates, counts, the second line of a card. `--spp-muted`. */
  inkMuted: "#888780",
  /** Labels and placeholders. DERIVED: `--spp-muted` lightened to sit under it. */
  inkFaint: "#b3b2ad",

  /** Cards and controls. */
  surface: "#ffffff",
  /** The page behind them. DERIVED: a warm near-white, so indigo ink is not on pure white. */
  canvas: "#f7f6f3",
  /** Inputs and unselected chips, so they read as recessed. `--spp-play-tint`. */
  sunken: "#eeedfe",

  /** Hairlines. DERIVED from the canvas, one step down. */
  line: "#e4e2dc",
  /** Borders on things you can press or type into. DERIVED, one step further. */
  lineStrong: "#cfccc4",

  /** Primary actions and links. `--spp-pass` -- green reads as go. */
  accent: "#1d9e75",
  /** `--spp-pass-deep`. */
  accentPressed: "#04342c",
  /** On the accent. */
  onAccent: "#ffffff",

  /** Destructive actions and failures. `--spp-shake`; the pack has no red. */
  danger: "#d85a30",
  /** `--spp-shake-tint`. */
  dangerSurface: "#faece7",
  /** DERIVED: between the tint and the hue, so the border reads without shouting. */
  dangerLine: "#f0cfc1",

  /** Won a game. `--spp-play` -- the celebratory one. */
  win: "#7f77dd",

  /** On a dark control. */
  onInk: "#ffffff",
};

/**
 * `--spp-radius-die` is 9 and `--spp-radius-icon` is 20 (WI-085). The die
 * radius is the one the interface repeats; the icon radius belongs to the mark
 * rather than to a control, so it is not a step.
 */
export const radius = {
  sm: 6,
  md: 9,
  lg: 14,
  pill: 999,
};

/** The grid both clients count in. Every space step is a multiple of it. */
export const SPACE_UNIT = 4;

/**
 * The steps both clients share.
 *
 * A phone and a laptop agree about small gaps -- the space inside a chip is
 * the same idea on both. They stop agreeing above this, which is why the web
 * adds its own larger steps rather than stretching these.
 */
export const space = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
};

/**
 * A card sits on the page rather than being drawn on it.
 *
 * Kept as parts rather than a rendered string, because the two clients need
 * different shapes from it: React Native wants `shadowOpacity`/`shadowRadius`
 * plus an Android-only `elevation`, and CSS wants one `box-shadow`. ADR-0025's
 * generator had to recover these by running a regex over `theme.ts` and
 * rebuilding the `rgba()` by hand.
 */
export const cardShadow = {
  colour: "#26215c",
  opacity: 0.06,
  blur: 12,
  offsetY: 3,
  /** Android only; ignored everywhere else. */
  elevation: 2,
};

/**
 * `--spp-font` from the pack.
 *
 * Mobile takes only the first name -- React Native wants a single family, not
 * a stack. The web ships Outfit itself (WI-092); before that it named the face
 * here and served nobody, because almost no reader has it installed.
 */
export const fontFamily = "Outfit";
export const fontStack =
  '"Outfit", "Poppins", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
